import * as THREE from 'three'
import { markRaw } from 'vue'
import { getDiceTheme } from './diceThemes.js'

const makeCanvas = (size) => {
  const canvas = document.createElement('canvas')
  canvas.width = canvas.height = size
  return canvas
}

/**
 * The die is composited onto the photographic chamber plate, so this builds no
 * room of its own — only what makes the die belong to that photo: a real cast
 * shadow on the stone and the ring's light spilling up onto its underside.
 */
export class DiceRitualEnvironment {
  constructor(theme = null) {
    this.theme = theme || getDiceTheme()
    this.group = markRaw(new THREE.Group())
    this.disposables = []
    this.elapsed = 0
    this.contactGlow = null
    this.ringLight = null
  }

  build(scene) {
    // No fog here on purpose: atmosphere is a full-screen overlay on the plate
    // (see ChamberFog), so it drifts across the whole room, not just this canvas.
    this.buildShadowCatcher()
    this.buildContactGlow()
    this.buildLights()
    scene.add(this.group)
    return this.group
  }

  track(...objects) {
    this.disposables.push(...objects)
  }

  /**
   * Invisible except where the die's shadow falls, so the shadow lands directly
   * on the photographed stone.
   */
  buildShadowCatcher() {
    const geometry = new THREE.PlaneGeometry(16, 16)
    const material = new THREE.ShadowMaterial({ opacity: 0.55 })
    const catcher = new THREE.Mesh(geometry, material)
    catcher.rotation.x = -Math.PI / 2
    catcher.receiveShadow = true
    this.group.add(catcher)
    this.track(geometry, material)
  }

  buildContactGlow() {
    const size = 512
    const canvas = makeCanvas(size)
    const ctx = canvas.getContext('2d')
    const [core, falloff] = this.theme.environment.contactGlow
    const gradient = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2)
    gradient.addColorStop(0, core)
    gradient.addColorStop(0.4, falloff)
    gradient.addColorStop(1, 'rgba(0, 0, 0, 0)')
    ctx.fillStyle = gradient
    ctx.fillRect(0, 0, size, size)

    const texture = new THREE.CanvasTexture(canvas)
    texture.colorSpace = THREE.SRGBColorSpace

    const material = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false
    })

    this.contactGlow = new THREE.Mesh(new THREE.PlaneGeometry(4.2, 4.2), material)
    this.contactGlow.rotation.x = -Math.PI / 2
    this.contactGlow.position.y = 0.012
    this.group.add(this.contactGlow)
    this.track(this.contactGlow.geometry, material, texture)
  }

  buildLights() {
    // The plate's ring glows from within, so the die is lit from below as much
    // as above — that uplight is what sells it as standing in the circle.
    const { ringLight, ringLightIntensity } = this.theme.environment
    this.baseRingIntensity = ringLightIntensity
    this.ringLight = new THREE.PointLight(ringLight, ringLightIntensity, 9, 2)
    this.ringLight.position.set(0, 0.18, 0)
    this.group.add(this.ringLight)
  }

  update(deltaTime) {
    this.elapsed += deltaTime

    if (this.contactGlow) {
      const breathe = 0.92 + Math.sin(this.elapsed * 0.7) * 0.1
      this.contactGlow.scale.setScalar(breathe)
    }

    if (this.ringLight) {
      this.ringLight.intensity = this.baseRingIntensity - 1 + Math.sin(this.elapsed * 1.1) * 2
    }

  }

  dispose() {
    this.disposables.forEach((item) => item?.dispose?.())
    this.disposables = []
    this.contactGlow = null
    this.ringLight = null
    this.group.clear()
  }
}

export default DiceRitualEnvironment
